import { QUINN_CHAT_TITLE_MAX_LENGTH } from "../types";

const PANEL_PREFS_KEY_PREFIX = "quinn:panel";
const SELECTED_CLIENT_KEY_PREFIX = "quinn:client";
const QUINN_SESSION_STORAGE_PREFIX = "quinn:";

export const ALL_CLIENTS_STORED_VALUE = "all";

function panelPrefsKey(userId: string): string {
    return `${PANEL_PREFS_KEY_PREFIX}:${userId}`;
}

function selectedClientKey(userId: string): string {
    return `${SELECTED_CLIENT_KEY_PREFIX}:${userId}`;
}

export const QUINN_MIN_WIDTH = 380;
export const QUINN_MAX_WIDTH = 720;
export const QUINN_DEFAULT_WIDTH = 440;
export const QUINN_PANEL_GUTTER = 8;
export const QUINN_PANEL_TOP = 8;

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isUuid(value: string | null | undefined): value is string {
    return typeof value === "string" && UUID_RE.test(value);
}

export function isServerChatId(chatId: string | null | undefined): chatId is string {
    return isUuid(chatId);
}

export type QuinnPanelPrefs = {
    isOpen: boolean;
    width: number;
};

const DEFAULT_PANEL_PREFS: QuinnPanelPrefs = {
    isOpen: false,
    width: QUINN_DEFAULT_WIDTH,
};

export function clampPanelWidth(width: number): number {
    if (Number.isNaN(width)) return QUINN_DEFAULT_WIDTH;
    return Math.min(QUINN_MAX_WIDTH, Math.max(QUINN_MIN_WIDTH, Math.round(width)));
}

export function readPanelPrefs(userId: string): QuinnPanelPrefs {
    try {
        const raw = localStorage.getItem(panelPrefsKey(userId));
        if (!raw) return DEFAULT_PANEL_PREFS;
        const parsed = JSON.parse(raw) as Partial<QuinnPanelPrefs>;
        return {
            isOpen: Boolean(parsed.isOpen),
            width: clampPanelWidth(Number(parsed.width ?? QUINN_DEFAULT_WIDTH)),
        };
    } catch {
        return DEFAULT_PANEL_PREFS;
    }
}

export function writePanelPrefs(userId: string, prefs: QuinnPanelPrefs): void {
    try {
        localStorage.setItem(panelPrefsKey(userId), JSON.stringify(prefs));
    } catch {
        return;
    }
}

export function readSelectedClientId(userId: string): string | null {
    try {
        const stored = sessionStorage.getItem(selectedClientKey(userId));
        if (stored === ALL_CLIENTS_STORED_VALUE) return stored;
        return isUuid(stored) ? stored : null;
    } catch {
        return null;
    }
}

export function writeSelectedClientId(userId: string, clientBusinessId: string | null): void {
    try {
        if (clientBusinessId == null) sessionStorage.removeItem(selectedClientKey(userId));
        else sessionStorage.setItem(selectedClientKey(userId), clientBusinessId);
    } catch {
        return;
    }
}

export function clearQuinnSession(): void {
    try {
        const keys = Object.keys(sessionStorage).filter((key) => key.startsWith(QUINN_SESSION_STORAGE_PREFIX));
        keys.forEach((key) => sessionStorage.removeItem(key));
    } catch {
        /* ignore storage failures */
    }
}

export function deriveChatTitle(firstUserMessage: string): string {
    const cleaned = firstUserMessage.trim().replace(/\s+/g, " ");
    if (cleaned.length <= QUINN_CHAT_TITLE_MAX_LENGTH) return cleaned;
    return `${cleaned.slice(0, QUINN_CHAT_TITLE_MAX_LENGTH - 1).trimEnd()}…`;
}
